import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { IoMdClose } from "react-icons/io";
import { usePostStore } from "../store/PostStore";
import { useAuthStore } from "../store/AuthStore";
import { usePageStore } from "../store/PageStore";
import { FiUpload } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { Loader, Sparkles } from "lucide-react";
import RichTextEditor from "./RichTextEditor";

const UpdatePostHandler = ({ post,setPostUpdated }) => {
   const { isUpdatePostOpen, setIsUpdatePostOpen } = usePageStore();
   const { updatePost, categoriesList, fetchCategories, fetchPosts, aiGenerateMeta } = usePostStore();
   const { authUser } = useAuthStore();
   const { register, handleSubmit, setValue, getValues, reset } = useForm();
   const [selectedCategories, setSelectedCategories] = useState([]);
   const [previewURL, setPreviewUrl] = useState(null);
   const [loading, setLoading] = useState(false);
   const [metaLoading, setMetaLoading] = useState(false);
   const [editorContent, setEditorContent] = useState("");
   const imageref = useRef();
   const [categories, setCategories] = useState([]);

      
   useEffect(() => {
      const fetchCategoryAndPostfromStore = async()=>{
         const array = await fetchCategories();
         setCategories(array);
         // console.log(array)
      }
      fetchCategoryAndPostfromStore(); // will updaate Zustand store
   }, [fetchCategories,categoriesList]);

  const initialized = useRef(false);

   useEffect(() => {
      if (post && !initialized.current) {
         initialized.current = true;

         const categoryNames = post.categories?.map((cat) => cat?.name).filter(Boolean) || [];
         setSelectedCategories(categoryNames);

         setValue("title", post.title);
         setEditorContent(post.content || "");
         setValue("readTime", post.readTime);
         setValue("image", post.image);
         setPreviewUrl(post.image);
      }
   }, [post, setValue]);

   const handleClose = () => {
      setIsUpdatePostOpen(false);
      // navigate(/profile/);
   };

   const onSubmit = async (data) => {
      if (selectedCategories.length === 0) {
         toast.error("Please select at least one category!");
         return;
      }
      if (!editorContent || editorContent === "<p></p>") {
         toast.error("Please write some content!");
         return;
      }

      const formData = new FormData();
      formData.append("title", data.title);
      formData.append("content", editorContent);
      formData.append("readTime", data.readTime);
      formData.append("author", authUser._id);
      formData.append("_id", post._id);

      if (data.image instanceof File) {
         formData.append("image", data.image);
      }

      selectedCategories.forEach((cat) => {
         formData.append("categories", cat);
      });
      
      setLoading(true);
      // console.log("FormData in update:");
      // for (let pair of formData.entries()) {
      // console.log(pair[0] + ": " + pair[1]);
      // }
      await updatePost(formData,post._id);
      setLoading(false);
      reset();
      setSelectedCategories([]);
      setEditorContent("");
      setIsUpdatePostOpen(false);
      fetchPosts();
      setPostUpdated(true);
   };

   const handleCategorySelect = (e) => {
      const value = e.target.value;
      if (value && !selectedCategories.includes(value)) {
         setSelectedCategories((prev) => [...prev, value]);
      }
   };

   const handleRemoveCategory = (cat) => {
      // console.log("here")
      setSelectedCategories((prev) => prev.filter((catId) => catId !== cat));
   };

   const handleImageClick = () => {
      imageref.current?.click();
   };

   const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
         setValue("image", file);
         setPreviewUrl(URL.createObjectURL(file));
      }
   };

   const handleImageClose = () => {
      setValue("image", null);
      setPreviewUrl(null);
   };

   const handleGenerateMeta = async () => {
      if (!editorContent || editorContent === "<p></p>" || editorContent.length < 30) {
         toast.error("Write some content first before generating title and summary");
         return;
      }
      setMetaLoading(true);
      try {
         const meta = await aiGenerateMeta(editorContent);
         if (meta) {
            if (!getValues("title")) {
               setValue("title", meta.title);
            }
            const plainText = editorContent.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
            const wordCount = plainText.split(/\s+/).length;
            const readTimeMin = Math.max(1, Math.ceil(wordCount / 200));
            setValue("readTime", readTimeMin);
            toast.success("Title generated and read time calculated!");
         }
      } finally {
         setMetaLoading(false);
      }
   };

   if (!isUpdatePostOpen || !post) return null;

   return (
      <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-[1px] bg-opacity-40">
         <div className="w-1/2 max-h-[75vh] overflow-y-auto rounded-lg p-6 accent-bg-mode shadow-accent-box relative">
         <button
            className="absolute hover:rotate-90 hover:scale-75 transition-all duration-500 top-4 right-4 text-2xl text-red-500"
            onClick={handleClose}
         >
            <IoMdClose />
         </button>

         <h2 className="text-2xl font-semibold mb-4 accent-text">Update Blog</h2>

         <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
               <div className="flex items-center justify-between mb-1">
                  <label className="accent-text">Title</label>
                  <button
                     type="button"
                     onClick={handleGenerateMeta}
                     disabled={metaLoading}
                     className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border accent-border hover:accent-bg-light transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                     title="Generate title and summary from content"
                  >
                     {metaLoading ? (
                        <Loader className="w-3.5 h-3.5 animate-spin" />
                     ) : (
                        <Sparkles className="w-3.5 h-3.5" />
                     )}
                     {metaLoading ? "Generating..." : "AI Generate"}
                  </button>
               </div>
               <input
               type="text"
               {...register("title", { required: true })}
               className="w-full px-4 py-2 border rounded-lg accent-border accent-bg-mode accent-text-mode"
               placeholder="Post Title"
               />
            </div>

            <div>
               <label className="accent-text block mb-1">Author</label>
               <input
               type="text"
               disabled
               value={authUser?.profile?.name}
               className="w-full text px-4 py-2 accent-text-mode text-opacity-20 font-normal border rounded-lg accent-border"
               />
            </div>

            <div>
               <label className="accent-text block mb-1">Content</label>
               <RichTextEditor
               content={editorContent}
               onChange={setEditorContent}
               placeholder="Write your content here..."
               />
            </div>

            <div>
               <label className="accent-text block mb-1">Cover Image</label>
               <div className="border accent-border p-5 rounded-lg">
               <input
                  type="file"
                  accept="image/*"
                  {...register("image")}
                  ref={imageref}
                  onChange={handleFileChange}
                  className="hidden"
               />
               <div
                  className="w-full cursor-pointer flex items-center gap-2 px-4 py-1 border-b rounded-lg accent-border accent-bg-mode accent-text-mode"
                  onClick={handleImageClick}
               >
                  <span className="text-red-500 text-base">
                     <FiUpload />
                  </span>
                  Select image <span className="opacity-40">(only 1 image allowed)</span>
               </div>

               {previewURL && (
                  <div className="relative inline-block mt-3">
                     <span
                     onClick={handleImageClose}
                     className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full accent-bg-mode text-red-600 font-bold hover:bg-red-50 cursor-pointer transition-transform hover:scale-110"
                     >
                     <IoMdClose size={16} />
                     </span>
                     <img
                     src={previewURL}
                     alt="Preview"
                     className="rounded-lg max-h-64 object-contain border"
                     />
                  </div>
               )}
               </div>
            </div>

            <div>
               <label className="accent-text block mb-1">Read Time</label>
               <input
               type="number"
               {...register("readTime", { required: true, min: 1 })}
               className="w-full px-4 py-2 border rounded-lg accent-border"
               placeholder="e.g. 5"
               />
            </div>

            <div>
               <label className="accent-text block mb-1">Categories</label>
               <select
               onChange={handleCategorySelect}
               className="w-full px-4 py-2 border accent-text-mode accent-bg-mode rounded-lg accent-border"
               defaultValue=""
               >
               <option className="accent-text-mode accent-bg-mode" value="" disabled>Select a category</option>
               {categories?.map((cat,index) => (
                  <option className="accent-text-mode accent-bg-mode" key={index} value={cat}>{cat}</option>
               ))}
               </select>

               <div className="flex flex-wrap gap-2 mt-2">
               {selectedCategories.map((cat,index) => (
                  <div key={index} className="flex items-center accent-bg-light accent-text-mode accent-bg-mode px-3 py-1 rounded-full text-sm">
                     {cat}
                     <button
                        type="button"
                        onClick={() => handleRemoveCategory(cat)}
                        className="hover:scale-105 transition-all duration-300 ml-2 text-red-600 font-extrabold"
                     >
                        <IoMdClose />
                     </button>
                     </div>
                  )
               )}
               </div>
            </div>

            <button
               type="submit"
               disabled={loading}
               className="w-full flex justify-center items-center gap-2 accent-bg text-white px-6 py-2 rounded-lg shadow-accent-box"
            >
               {loading ? (
               <>
                  <Loader className="animate-spin w-5 h-5" />
                  <span>Updating...</span>
               </>
               ) : (
               "Update Post"
               )}
            </button>
         </form>
         </div>
      </div>
   );
};

export default UpdatePostHandler;
